// ONE-CLICK [temporary — see docs/refactoring/ONE-CLICK-UPGRADE-FEATURE.md]
//
// Pure helpers for the One Click upgrade feature (Solarman inverters).
// No Angular / RxJS deps — trivially inspectable and droppable in one folder.
//
// The one-click ready-vs-armFirst decision is authoritative from the Solarman
// Pro `support-types` API (COMBINATION present = one-click available now), NOT
// from firmware version comparison (some families changed versioning scheme,
// e.g. ZM2 old line V6xxxxx vs new V07xxxx, which breaks numeric compare).
// `MIN_ONE_CLICK_ARM` in config is only a per-family kill-switch + info value.

import { ChipData, InverterUpgradeConfig, fwVersionLetter } from 'ngx-modbus';

/** Communication channel as parsed by parseOnlineStatus (inverter-status.utils). */
export type OneClickChannel = 'MQTT' | 'Ethernet' | null;

export type OneClickState = 'unsupported' | 'armFirst' | 'ready' | 'unknown';

export type OneClickReason =
  | 'noMinField' // family has no MIN_ONE_CLICK_ARM → feature off (legacy flow)
  | 'ethernetChannel' // stick Ethernet → one-click not supported yet
  | 'combinationAvailable' // support-types has COMBINATION → ready
  | 'singleOnly' // support-types has only SINGLE → update ARM first
  | 'supportCheckFailed'; // support-types call failed → unknown, retry

export interface OneClickGate {
  state: OneClickState;
  reason?: OneClickReason;
  /** Informational: family minimum ARM for one-click (from config). */
  minArmVersion?: string;
}

/** Response of GET /order-s/order/action/firmware/device/support-types. */
export interface SolarmanSupportTypes {
  types: Array<{
    code: 'SINGLE' | 'COMBINATION';
    supportSafetyStandardUpdate?: boolean | null;
    supportAfciUpgrade?: boolean | null;
    supportTmuUpgrade?: boolean | null;
  }>;
}

/** Per-inverter push snapshot, from the NgRx store (subscription) or the polling fallback. */
export interface OneClickPushState {
  arm: string | null;
  updating: boolean;
  /**
   * Legacy/iot FOTA flag ('1' running, '0' idle). The ARM-first step signals
   * ONLY here: on the iot path the cloud leaves updating/upd_global null.
   */
  fotaStatus: string | null;
  updGlobal: { status: number; progress: number; createdAt?: number } | null;
}

export type OneClickModuleKind = 'ARM' | 'DSPM' | 'DSPS' | 'SAFETY';

export interface OneClickModulePlan {
  module: OneClickModuleKind;
  link: string;
  /** Target version (LATEST_VERSION); absent for SAFETY. */
  expectedVersion?: string;
  currentVersion?: string;
}

export type OneClickCurrentVersions = Partial<Record<'ARM' | 'DSPM' | 'DSPS', string>>;

/**
 * Decide the one-click state for an inverter.
 *
 * @param config       family config resolved from S3 (or null if unresolved)
 * @param channel      communication channel ('MQTT' | 'Ethernet' | null)
 * @param supportTypes result of the Solarman support-types call, or null if the
 *                     call failed / was not performed
 */
export const evaluateOneClickGate = (
  config: Pick<InverterUpgradeConfig, 'MIN_ONE_CLICK_ARM'> | null | undefined,
  channel: OneClickChannel,
  supportTypes: SolarmanSupportTypes | null,
): OneClickGate => {
  const minArmVersion = config?.MIN_ONE_CLICK_ARM;

  // Kill-switch: family not enabled → legacy flow untouched.
  if (!minArmVersion) {
    return { state: 'unsupported', reason: 'noMinField' };
  }

  // Ethernet stick: one-click not supported yet (even with Azzurro fw) → legacy.
  if (channel === 'Ethernet') {
    return { state: 'unsupported', reason: 'ethernetChannel', minArmVersion };
  }

  // support-types unavailable → can't decide; never fall back to legacy table.
  if (!supportTypes) {
    return { state: 'unknown', reason: 'supportCheckFailed', minArmVersion };
  }

  const hasCombination = supportTypes.types?.some((t) => t.code === 'COMBINATION') ?? false;
  if (hasCombination) {
    return { state: 'ready', reason: 'combinationAvailable', minArmVersion };
  }

  // Only SINGLE: update ARM to latest; re-check support-types afterwards.
  return { state: 'armFirst', reason: 'singleOnly', minArmVersion };
};

/**
 * Pick the ChipData for a module matching the current version's chip letter.
 * Single-entry configs are used as-is; multi-chip configs require a letter match.
 * Returns undefined when no safe pick is possible.
 */
const pickChip = (entries: ChipData[] | undefined, currentVersion: string | undefined): ChipData | undefined => {
  if (!entries || entries.length === 0) {
    return undefined;
  }
  if (entries.length === 1) {
    return entries[0];
  }
  if (!currentVersion) {
    return undefined;
  }
  return entries.find((fw) => fwVersionLetter(fw.LATEST_VERSION) === fwVersionLetter(currentVersion));
};

/**
 * Resolve the ARM-only upgrade (chip-matched to the current ARM) for the
 * armFirst step. Returns null when no ARM candidate exists → caller shows
 * "blocked" (never sends a command blindly).
 */
export const resolveArmUpgrade = (
  config: InverterUpgradeConfig | null | undefined,
  currentArm: string | undefined,
): OneClickModulePlan | null => {
  const chip = pickChip(config?.ARM, currentArm);
  if (!chip?.LINK) {
    return null;
  }
  return { module: 'ARM', link: chip.LINK, expectedVersion: chip.LATEST_VERSION, currentVersion: currentArm };
};

/**
 * Build the 4-module one-click plan (ARM, DSPM, DSPS, SAFETY[0]).
 * Returns null if any of the four is missing → caller shows a "blocked" state
 * instead of ever sending a partial upgrade.
 */
export const buildOneClickModulePlan = (
  config: InverterUpgradeConfig | null | undefined,
  currentVersions: OneClickCurrentVersions,
): OneClickModulePlan[] | null => {
  if (!config) {
    return null;
  }

  const plan: OneClickModulePlan[] = [];

  const moduleKinds: Array<'ARM' | 'DSPM' | 'DSPS'> = ['ARM', 'DSPM', 'DSPS'];
  for (const kind of moduleKinds) {
    const current = currentVersions[kind];
    const chip = pickChip(config[kind], current);
    if (!chip?.LINK) {
      return null;
    }
    plan.push({
      module: kind,
      link: chip.LINK,
      expectedVersion: chip.LATEST_VERSION,
      currentVersion: current,
    });
  }

  const safety = config.SAFETY?.[0];
  if (!safety?.LINK) {
    return null;
  }
  plan.push({ module: 'SAFETY', link: safety.LINK });

  return plan;
};
